import {
  BookingSourceType,
  BookingState,
  Gender,
  Guardian,
  Gym,
  ISignupSource,
  Level,
  ProgramType,
  StrengthRatioForMuscleGroups,
  WorkoutType,
} from 'fitbeat.models';
import { AreaCategory } from '../../config/zone-mappings';
import { GymSchedulingConfig } from '../../config/gymscheduling';
import { ScreenSession, SessionSequenceStrategyContext } from '../../logic';
import { ExerciseFormat } from '../exercise';
import {
  ExerciseSessionType,
  SessionSequence,
  UserExerciseSession,
  UserWorkoutSession,
  UserWorkoutSessionWithDisplayInfo,
  UserWorkoutStatus,
} from '../workoutsession';
import { GymState } from './gymstate';
import { Side, Zone } from './zone';

// todo should ideally be in models not core, but need to move dependencies first
export interface IGymStateViewModel {
  stateTime: Date;
  endTime: Date;
  sessions: IGymStateSessionViewModel[];
}

export interface IScreenSection {
  data: ScreenSession[];
  heading?: string;
  gymName: string;
  currentQueueSlice: Date;
  nextClassTime?: Date;
}

export interface IGymStateSessionViewModel {
  userProfile: IUserWorkoutDisplayInfo | IGymStateAnonymousDisplayInfo;
  workoutSessionId: string | null;
  removedAt: Date | null;
  checkedIn: boolean;
  workoutType: number;
  amountOfStages: number;
  currentStage: number;
  cycleTimeInSeconds: number;
  sessionSequence: SessionSequence[];
  workoutDescription: string;
  bookingState: BookingState | null;
  format: ExerciseFormat;
  areaCategory: AreaCategory;
  zoneNumber: number;
  displayNumber: string;
  nextZoneNumber: number;
  zoneName: string;
  type: ExerciseSessionType;
  isEmpty: boolean;
  stationNumber: number;
  isNewUser: boolean;
  side: Side;
}

export interface IUserWorkoutDisplayInfo {
  receivingHeartRateMonitorData: any; // todo move out of user info - one level higher
  uid: string; // todo redundant copy of userProfileId should be removed
  userWorkoutStatus: UserWorkoutStatus;
  userProfileId: string;
  avatar: string;
  name: string;
  fullname: string;
  nickname: string;
  bookingSource: BookingSourceType;
  isPastWorkout: boolean;
  isNewUser: boolean;
  acceptedTermsAndConditions: boolean;
  startZone: number;
  mobileNumber: string;
  heartRateMonitorIdentifier: string;
  ptApprovedAt: number;
  currentPlanId: string | undefined;
  currentPlanStatus: string | undefined;
  dateOfBirth: string;
  gender: Gender;
  weight: number | undefined;
  height: number | undefined;
  guardianInfo?: Guardian;
  signupSource: ISignupSource;
  longTermGoal?: ProgramType;
  skillLevel?: Level;
  continueSignupLink?: string;
  enrolledFaceId?: string;
  leadStatus?: string;
  email: string;
  strengthRatioForMuscleGroups?: StrengthRatioForMuscleGroups;
}

export interface IGymStateAnonymousDisplayInfo {
  name: string;
  fullname: string;
  userWorkoutStatus: UserWorkoutStatus;
  uid: string;
}

export class MapGymStateToViewModel {
  public static async mapGymStateScheduleToViewModels(
    schedule: GymState[],
    gym: Gym,
    recentlySeenUsers: string[],
  ): Promise<IGymStateViewModel[]> {
    return Promise.all<IGymStateViewModel>(
      schedule.map(
        (gymState) => MapGymStateToViewModel.mapGymStateAtTimeSliceToViewModel(gymState, gym, recentlySeenUsers),
      ),
    );
  }

  private static async mapGymStateAtTimeSliceToViewModel(
    gymState: GymState,
    gym: Gym,
    recentlySeenUsers: string[],
  ): Promise<IGymStateViewModel> {
    const sessions = await Promise.all<IGymStateSessionViewModel>(
      gymState.sessions.map(
        async (session: UserExerciseSession) => MapGymStateToViewModel.mapUserExerciseSessionToViewModel(
          session,
          gym,
          recentlySeenUsers,
        ),
      ),
    );
    return {
      stateTime: gymState.stateTime,
      endTime: gymState.endTime,
      sessions,
    };
  }

  private static mapUserExerciseSessionToViewModel(
    session: UserExerciseSession,
    gym: Gym,
    recentlySeenUsers: string[],
  ): IGymStateSessionViewModel {
    const parentWorkoutSession = session.parentWorkoutSession as UserWorkoutSessionWithDisplayInfo | undefined;
    // TODO: this is recalculating the zone from the stationNumber, but session.zone exist. will cause issues
    // when zone mappings change after bookings
    const zone = Zone.zoneForStation(session.stationNumber, gym);

    return {
      userProfile: MapGymStateToViewModel.mapUserDisplayInfo(session, parentWorkoutSession, recentlySeenUsers),
      workoutSessionId: parentWorkoutSession ? parentWorkoutSession.uniqueReference : null,
      removedAt: parentWorkoutSession && parentWorkoutSession.removedAt ? parentWorkoutSession.removedAt : null,
      checkedIn: parentWorkoutSession ? parentWorkoutSession.checkedIn : false,
      workoutType: parentWorkoutSession ? parentWorkoutSession.workoutType
        : WorkoutType.MediumStudioWorkout,
      amountOfStages: parentWorkoutSession ? parentWorkoutSession.amountOfStages() : 0,
      currentStage: session.workoutStage,
      cycleTimeInSeconds: GymSchedulingConfig.cycleTimeInSeconds,
      sessionSequence: SessionSequenceStrategyContext.getSessionSequenceForStudio(
        session,
        gym.type,
        parentWorkoutSession?.settings,
      ),
      workoutDescription: UserWorkoutSession.getWorkoutDescription(parentWorkoutSession),
      bookingState: parentWorkoutSession ? parentWorkoutSession.bookingState : null,
      format: session.format,
      areaCategory: zone.areaCategory,
      zoneName: zone.name,
      zoneNumber: zone.numberInGym,
      displayNumber: zone.displayNumber,
      nextZoneNumber: zone.nextZoneNumber,
      type: session.type,
      isEmpty: session.isEmpty(),
      stationNumber: session.stationNumber,
      isNewUser: parentWorkoutSession ? parentWorkoutSession.isNewUser : false,
      side: zone.side,
    };
  }

  private static mapUserDisplayInfo(
    exerciseSession: UserExerciseSession,
    workoutSession: UserWorkoutSessionWithDisplayInfo | undefined,
    recentlySeenUsers: string[],
  ): IUserWorkoutDisplayInfo | IGymStateAnonymousDisplayInfo {
    if (workoutSession && (exerciseSession.type === ExerciseSessionType.User)) {
      let userDisplayInfo = MapGymStateToViewModel.mapWorkoutUserInfoToViewModel(workoutSession);
      const receivingHeartRateMonitorData = recentlySeenUsers.includes(userDisplayInfo.userProfileId);
      // todo receivingHeartRateMonitorData should ideally be injected one level higher
      userDisplayInfo = { ...userDisplayInfo, receivingHeartRateMonitorData };
      return userDisplayInfo;
    }
    if (exerciseSession.type === ExerciseSessionType.Anonymous) {
      return {
        name: 'Anonymous',
        fullname: 'Anonymous',
        userWorkoutStatus: UserWorkoutStatus.Other,
        uid: '',
      };
    }
    return {
      name: '',
      fullname: '',
      userWorkoutStatus: UserWorkoutStatus.Other,
      uid: '',
    };
  }

  // todo simply extract the IUserWorkoutDisplayInfo properties - can be automated
  private static mapWorkoutUserInfoToViewModel(
    workoutSession: UserWorkoutSessionWithDisplayInfo,
  ): IUserWorkoutDisplayInfo {
    return {
      name: workoutSession.name,
      fullname: workoutSession.fullname,
      nickname: workoutSession.nickname,
      bookingSource: workoutSession.bookingSource,
      isPastWorkout: workoutSession.isPastWorkout,
      startZone: workoutSession.startZone,
      mobileNumber: workoutSession.mobileNumber,
      gender: workoutSession.gender,
      acceptedTermsAndConditions: workoutSession.acceptedTermsAndConditions,
      uid: workoutSession.uid,
      userProfileId: workoutSession.userProfileId,
      avatar: workoutSession.avatar,
      userWorkoutStatus: workoutSession.userWorkoutStatus,
      currentPlanStatus: workoutSession.currentPlanStatus,
      currentPlanId: workoutSession.currentPlanId,
      enrolledFaceId: workoutSession.enrolledFaceId,
      heartRateMonitorIdentifier: workoutSession.heartRateMonitorIdentifier,
      receivingHeartRateMonitorData: undefined,
      weight: workoutSession.weight,
      height: workoutSession.height,
      guardianInfo: workoutSession.guardianInfo,
      dateOfBirth: workoutSession.dateOfBirth,
      leadStatus: workoutSession.leadStatus,
      ptApprovedAt: workoutSession.ptApprovedAt,
      signupSource: workoutSession.signupSource,
      longTermGoal: workoutSession.longTermGoal,
      skillLevel: workoutSession.skillLevel,
      isNewUser: workoutSession.isNewUser,
      continueSignupLink: workoutSession.continueSignupLink,
      email: workoutSession.email,
    };
  }
}
